import { Button } from "@mui/material"
import { useState } from "react"
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Localized } from "@fluent/react"

export interface RequirementDescriptionProps {
    description: string
    // The active search text, so a body whose match lies past the first paragraph
    // can open itself. Without it a requirement passes the filter, gets a row, and
    // shows nothing highlighted — which reads as a broken search.
    searchText?: string
}

// Crude but deliberate: the same tag strip requirementFilter falls back to, so
// what counts as "present in this paragraph" here agrees with what counted as a
// match there.
function plainText(html: string): string {
    return html.replace(/<[^>]*>/g, ' ').toLowerCase()
}

type RequirementDescriptionStyle
    = { tag: 'single-paragraph', fullContent: string }
    | { tag: 'multi-paragraph', firstParagraph: string, fullContent: string }
    | { tag: 'unknown-format', fullContent: string }

function parseStyle(description: string): RequirementDescriptionStyle {
    const matches = description.match(/<p>.*?<\/p>|<ol>.*?<\/ol>|<ul>.*?<\/ul>/g)
    if (matches === null) {
        return { tag: 'unknown-format', fullContent: description }
    } else if (matches.length === 1) {
        return { tag: 'single-paragraph', fullContent: description }
    } else {
        return { tag: 'multi-paragraph', firstParagraph: matches[0], fullContent: description }
    }
}

export const RequirementDescription = ({ description, searchText }: RequirementDescriptionProps) => {
    const styledDescription = parseStyle(description)
    switch (styledDescription.tag) {
    case 'single-paragraph':
        return <SingleParagraph fullContent={styledDescription.fullContent} />
    case 'multi-paragraph':
        return <MultiParagraph
            firstParagraph={styledDescription.firstParagraph}
            fullContent={styledDescription.fullContent}
            searchText={searchText}
        />
    case 'unknown-format':
        return <div dangerouslySetInnerHTML={{ __html: description}}></div>
    }
}

// True when the search would find something in the body but not in the paragraph
// that is shown while collapsed. Requires the match to exist in the full markup
// too: a requirement can match the filter through `searcheableContent` while the
// rendered HTML holds no such text, and expanding that one would reveal nothing.
export function matchLiesBeyondFirstParagraph(
    firstParagraph: string,
    fullContent: string,
    searchText: string | undefined,
): boolean {
    const needle = (searchText ?? '').trim().toLowerCase()
    if (needle === '') return false
    return !plainText(firstParagraph).includes(needle) && plainText(fullContent).includes(needle)
}

const SingleParagraph = ({ fullContent }: { fullContent: string }) => {
    return <div dangerouslySetInnerHTML={{ __html: fullContent }}></div>
}

const MultiParagraph = ({ firstParagraph, fullContent, searchText }: { firstParagraph: string, fullContent: string, searchText?: string }) => {
    const [expanded, setExpanded] = useState(false)
    const handleShowMoreClick = () => {
        setExpanded(true)
    }
    const handleShowLessClick = () => {
        setExpanded(false)
    }
    // Forced open rather than merely defaulted open: collapsing would hide the very
    // text the search found, so while that search is active there is no useful
    // collapsed state to offer and the toggle is dropped.
    const forcedOpen = matchLiesBeyondFirstParagraph(firstParagraph, fullContent, searchText)
    const showFull = expanded || forcedOpen
    return <>
        <div dangerouslySetInnerHTML={{ __html: showFull ? fullContent : firstParagraph }}></div>
        {!showFull && <Button startIcon={<ExpandMoreIcon />} onClick={handleShowMoreClick} size="small"><Localized id="show-more"><span>Show more</span></Localized></Button>}
        {showFull && !forcedOpen && <Button startIcon={<ExpandLessIcon />} onClick={handleShowLessClick} size="small"><Localized id="show-less"><span>Show less</span></Localized></Button>}
    </>
}

const Empty = () => {
    return <></>
}
